/**
 * Axios request interceptor for impersonation grants (#1137 Task 3.2).
 *
 * Reads the active grant from sessionStorage (written by
 * ImpersonationContext, Task 3.1) and stamps `X-Impersonation-Grant`
 * onto every outbound API request — except calls to the revoke
 * endpoint, which must run as the actor's own auth so the grant being
 * revoked isn't the one authenticating the call (B3 deadlock fix).
 *
 * Installed onto the shared `API` axios instance in App.js. The bare
 * function is exported so unit tests can invoke it without spinning up
 * the full app or an axios instance.
 *
 * Failure modes are silent on purpose: if sessionStorage is unavailable
 * (Safari private browsing, hardened clients) or the stored payload is
 * malformed, the request goes out unmodified. The actor's normal auth
 * (Authorization Bearer header from the existing interceptor) still
 * applies — they just lose impersonation, not access.
 */

const STORAGE_KEY = 'fray_impersonation';

// Match `/api/impersonate/grant/{anything-except-slash}/revoke` at the
// end of the URL. Tolerates absolute base URLs (e.g. when REACT_APP_BACKEND_URL
// is set and axios prepends it) by anchoring on the path tail rather than
// the full URL start.
const REVOKE_URL_RE = /\/api\/impersonate\/grant\/[^/]+\/revoke$/;

export function attachImpersonationHeader(config) {
  const url = (config && config.url) || '';
  if (REVOKE_URL_RE.test(url)) return config;

  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const ctx = JSON.parse(raw);
      if (ctx && ctx.grant_token) {
        config.headers = config.headers || {};
        config.headers['X-Impersonation-Grant'] = ctx.grant_token;
      }
    }
  } catch (_) {
    // sessionStorage unavailable or payload corrupt; proceed unmodified.
  }

  return config;
}
